import {EventEmitter, Injectable} from '@angular/core';
import {AuthService} from '../../../auth/services/auth/auth.service';
import {AppService} from '../../../app-common/services/app/app.service';
import {RequestsWebsocketService} from '../../../app-common/services/server/requests-websocket.service';
import {AddSnippetRequest} from '../../data/requests/addSnippetRequest';
import {EditSnippetRequest} from '../../data/requests/editSnippetRequest';
import {Snippet, SnippetType} from '../../data/snippet';
import {PleaseWaitService} from '../../../app-common/services/please-wait/please-wait.service';
import {EventEx} from '../../../app-common/data/Event';
import {Query} from '../../../query/data/query';
import {
  QueryCreator,
  QueryDTO,
  QueryFilter,
  QueryJoin,
  QuerySort,
  QueryUnion
} from '../../../query/data/queryCreator';
import {QueryableProperty} from '../../../query/data/queryableProperty';
import {EqualsValuePropertyFilter} from '../../../query/data/equalsValuePropertyFilter';
import {SnippetProperties} from '../../../query/data/specifics/snippetProperties';
import {ContainsValuePropertyFilter} from '../../../query/data/containsValuePropertyFilter';
import {SnippetDto} from '../../data/snippetDto';
import {Observable, Subject} from 'rxjs';
import {ApiService} from '../../../app-common/services/server/api.service';
import {ConflictException} from '../../../app-common/services/server/conflictException';
import {LogLevel} from '../../../logging/data/logLevel';
import {LogSourceType} from '../../../logging/data/logSourceType';
import {LogSource} from '../../../logging/data/logSource';
import {LoggerService} from '../../../logging/services/logger.service';
import {GenericVersion} from '../../../audit/data/GenericVersion';

@Injectable({
  providedIn: 'root'
})
export class SnippetService {
  onConflict: EventEx = new EventEx();
  openSnippetListPopupForCk: EventEmitter<EventEmitter<string>> = new EventEmitter<EventEmitter<string>>();

  private refreshListOnEdit = new Subject<void>();

  constructor(public authService: AuthService,
              public appService: AppService,
              public httpService: ApiService,
              public pleaseWaitService: PleaseWaitService,
              public websocket: RequestsWebsocketService,
              public loggingService: LoggerService) {
    this.authService = authService;
    this.appService = appService;
    this.websocket = websocket;
  }

  triggerRefreshSnippetList(): void {
    this.refreshListOnEdit.next();
  }

  listenToRefreshList(): Observable<void> {
    return this.refreshListOnEdit.asObservable();
  }

  getDummySnippet(): Snippet {
    const editSnippet = new Snippet();
    // Intentional BUG HERE for logging test
    // editSnippet = undefined;
    editSnippet.id = 0;
    editSnippet.html = '';
    editSnippet.name = '';
    editSnippet.type = SnippetType.Job;
    editSnippet.link = '';
    editSnippet.description = '';
    return editSnippet;
  }

  getDummySnippetDto(): SnippetDto {
    const editSnippetDto = new SnippetDto();
    editSnippetDto.snippet = this.getDummySnippet();
    editSnippetDto.categories = [];
    return editSnippetDto;
  }

  async getSnippet(snippetId: number, force: boolean = false): Promise<SnippetDto> {
    if (snippetId <= 0) {
      return undefined;
      this.loggingService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.SnippetService, 'Failed to fetch snippets!');
      // this.loggingService.logMessage(LogLevel.DEBUG, LogSourceType.SERVICE, LogSource.SnippetService, 'Failed to fetch snippets!');
    }
    return await this.httpService.applicationApiClient.getWithParams<SnippetDto>(`Snippet/${snippetId}`, {force});
  }

  async getSnippetHistory(snippetId: number, force: boolean = false): Promise<GenericVersion[]> {
    if (snippetId <= 0) {
      return undefined;
      this.loggingService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.SnippetService, 'Failed to fetch snippet audit!');
    }
    return await this.httpService.applicationApiClient.getWithParams<GenericVersion[]>(`Snippet/History/${snippetId}`, {force});
  }

  async getSnippetVersion(snippetId: number, version: number): Promise<SnippetDto> {
    if (snippetId <= 0 || version <= 0) {
      return undefined;
      this.loggingService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.SnippetService, 'Failed to fetch snippet audit!');
    }
    return await this.httpService.applicationApiClient.getWithParams<SnippetDto>(`Snippet/History/${snippetId}/${version}`, null);
  }

  async getSnippetVersionLabels(snippetId: number, version: number, force: boolean = false): Promise<string[]> {
    if (snippetId <= 0 || version <= 0) {
      return undefined;
    }
    try {
      return await this.httpService.applicationApiClient.getWithParams<string[]>(`Snippet/History/${snippetId}/${version}/Labels`, null);
    } catch (e) {
      return ['INITIAL'];
    }
  }

  async addSnippet(name: string, description: string, html: string, link: string, type: SnippetType, categories: number[]) {
    // const request = new SnippetDto();
    const request = new AddSnippetRequest();
    request.snippet = this.getDummySnippet();
    this.pleaseWaitService.addToQueue();
    request.snippet.name = name;
    request.snippet.description = description;
    request.snippet.html = html;
    request.snippet.link = link;
    request.snippet.type = type;
    request.categories = categories;

    let response;
    try {
      response = await this.httpService.applicationApiClient.post('Snippet', request);
      this.appService.showSuccessMessage('Snippet Added', {windowId: 'main'});
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      this.loggingService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.SnippetService, 'Failed to add snippet!' + ex);
      // this.loggingService.logMessage(LogLevel.DEBUG, LogSourceType.SERVICE, LogSource.SnippetService, 'Failed to add snippet!' + ex);
      return false;
    }
  }

  async editSnippet(dto: SnippetDto, force: boolean = false) {
    const request = new EditSnippetRequest();
    this.pleaseWaitService.addToQueue();
    request.snippet = dto.snippet;
    request.categories = dto.categories;
    request.force = force;

    let response;
    try {
      response = await this.httpService.applicationApiClient.put('Snippet', dto.snippet.id, request);
      this.appService.showSuccessMessage('Snippet Edited', {windowId: 'main'});
      this.pleaseWaitService.removeFromQueue();
      return response;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      if (ex instanceof ConflictException) {
        this.onConflict.emit({my: dto, db: ex.conflict});
      } else {
        this.loggingService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.SnippetService, 'Failed to edit snippet!' + ex);
        // this.loggingService.logMessage(LogLevel.DEBUG, LogSourceType.SERVICE, LogSource.SnippetService, 'Failed to edit snippet!' + ex);
      }
      return false;
    }
  }

  async revertSnippet(dto: SnippetDto, version: number): Promise<boolean> {
    let response;
    const snippetId = dto.snippet.id;
    try {
      response = await this.httpService.applicationApiClient.get(`Snippet/History/${snippetId}/${version}/Revert`);
      this.appService.showSuccessMessage('Snippet reverted', {windowId: 'main'});
      this.pleaseWaitService.removeFromQueue();
      return response;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      if (ex instanceof ConflictException) {
        this.onConflict.emit({my: dto, db: ex.conflict});
      } else {
        this.loggingService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.SnippetService, 'Failed to revert snippet!' + ex);
        // this.loggingService.logMessage(LogLevel.DEBUG, LogSourceType.SERVICE, LogSource.SnippetService, 'Failed to edit snippet!' + ex);
      }
      return false;
    }
  }

  async deleteSnippet(snippetId: number) {
    this.pleaseWaitService.addToQueue();
    try {
      const response = await this.httpService.applicationApiClient.delete(`Snippet`, snippetId);
      this.appService.showSuccessMessage('Snippet Deleted', {windowId: 'main'});
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      this.loggingService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.SnippetService, 'Failed to delete snippet!');
      // this.loggingService.logMessage(LogLevel.DEBUG, LogSourceType.SERVICE, LogSource.SnippetService, 'Failed to delete snippet!');
      return false;
    }
  }

  createEqualsFilter(property: QueryableProperty, value: any) {
    const result = new EqualsValuePropertyFilter();
    result.active = true;
    result.property = property;
    result.queryValue = value;
    return result;
  }

  createContainsFilter(property: QueryableProperty, toSearch: any) {
    const result = new ContainsValuePropertyFilter();
    result.active = true;
    result.property = property;
    result.queryValue = toSearch;
    return result;
  }

  buildSearchSnippetsFilter(query: string, entriesPerPage: number, pageNumber: number, useCategorySearch: boolean = false, categoryId: number = 0, useTypeSearch: boolean = false, type: SnippetType) {
    const customerProperties = new SnippetProperties();
    const rootQuery = new Query();
    for (const property of customerProperties.properties) {
      if (property.name === 'name') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
      if (property.name === 'description') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
      if (property.name === 'link') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
    }
    const sorting = new QuerySort();
    sorting.dir = customerProperties.sortingDescending ? 'desc' : 'asc';
    sorting.field = customerProperties.sortingPropertyName;

    const queryDTO = QueryCreator.generateFilter(rootQuery, sorting, entriesPerPage, pageNumber, 'or');
    if (useCategorySearch) {
      const join = new QueryJoin();
      join.externalTable = 'snippetCategories';
      join.internalTableKey = 'id';
      join.externalTableKey = 'snippetId';
      const joinFilter = new QueryFilter();
      joinFilter.field = 'categoryId';
      joinFilter.operator = 'eq';
      joinFilter.value = categoryId;
      join.filter = joinFilter;
      queryDTO.union[0].join = join;
    }
    if (useTypeSearch) {
      const queryFilterBackup = queryDTO.union[0].filter;
      const isLeadFilter = new QueryFilter();
      isLeadFilter.field = 'type';
      isLeadFilter.operator = 'eq';
      isLeadFilter.value = type;
      const rootFilter = new QueryFilter();
      rootFilter.filters = [];
      rootFilter.filters.push(queryFilterBackup, isLeadFilter);
      rootFilter.logic = 'and';
      queryDTO.union[0].filter = rootFilter;
    }

    return queryDTO;
  }

  buildGetJobSnippetsFilter(entriesPerPage: number, pageNumber: number) {
    const customerProperties = new SnippetProperties();
    const rootQuery = new Query();
    for (const property of customerProperties.properties) {
      if (property.name === 'type') {
        rootQuery.filters.push(this.createEqualsFilter(property, SnippetType.Job));
      }
    }
    const sorting = new QuerySort();
    sorting.dir = customerProperties.sortingDescending ? 'desc' : 'asc';
    sorting.field = customerProperties.sortingPropertyName;

    return QueryCreator.generateFilter(rootQuery, sorting, entriesPerPage, pageNumber, 'and');
  }

  async search(queryText, count: number, offset: number) {
    const dto = QueryDTO.create(offset, count, [QueryUnion.create(
        QueryFilter.group([
          QueryFilter.singleValue('name', 'contains', queryText),
          QueryFilter.singleValue('deleted', 'eq', 'false')
        ], 'and'))]
      , [QuerySort.create('name', 'asc')]);
    const response = await this.httpService.applicationApiClient.getWithParams<SnippetDto[]>('Snippet/Search', {filter: JSON.stringify(dto)});
    return response;
  }
}
